import React, { useMemo } from 'react';

import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import ExtensionContactAvatar, {
    initialsFromNames,
} from 'flow/components/ui-components/ExtensionContactAvatar';
import Icon from 'flow/components/ui-components/Icon';
import UserAvatar from 'flow/components/ui-components/UserAvatar';
import { AvatarSize } from 'flow/components/ui-components/ChatAvatar';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectExtensionKeyFromChatUserKey } from 'flow/selectors/chat-selectors';
import { makeSelectContactDataForNumber } from 'flow/selectors/contact-selectors';
import {
    selectContact,
    selectExtensionStatus,
} from 'flow/selectors/ExtensionSelectors';
import { Contact } from 'flow/types/Contact';
import { ChatUserKey, ExtensionKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { State } from 'flow/types/State';
import { memberColorClass } from 'flow/components/meeting/main-area/meeting-tile-utils';
import { sliceUUIDFromUserName } from 'flow/utils/meeting-utils';

interface Props {
    /** Set on a colleague, by home, at join (VID-264). */
    chatUserKey?: ChatUserKey;
    /** For a caller that already knows the extension, such as the self-view. */
    extensionKey?: ExtensionKey;
    phoneNumber?: string;
    displayName?: string;
    /** Home's embedded contact; only the lobby holds one, and for an
     *  unidentified participant it merely wraps their number. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    embeddedContact?: any;
    size?: AvatarSize;
    lazyLoad?: boolean;
    /** A dial-in participant, whose name is their number and so makes no
     *  initials — they get the glyph however they are named. */
    isCaller?: boolean;
    /** Drawn instead of the whole avatar when nobody resolved. */
    fallback?: React.ReactNode;
    showStatus?: boolean;
}

// Hoisted: a fresh element each render would defeat the memo below. Sized in em
// of the circle's own font size, which on a meeting tile scales with the tile:
// that puts the glyph at 48px on a 124px avatar, per Figma, and leaves it at
// its usual 24px on a fixed-size circle.
const PERSON_GLYPH = (
    <Icon iconKey="person" className="text-gray-400" size="2em" />
);

/**
 * A meeting participant's avatar, and the only place in the meeting that reads
 * contact data — callers pass identity and nothing else.
 *
 * A leaf on purpose: resolving somebody subscribes to stored contact data,
 * which both extension reducer paths replace wholesale on every refetch, so
 * this redraws then and the tile around it does not.
 */
const MeetingContactAvatar = ({
    chatUserKey,
    extensionKey: knownExtensionKey,
    phoneNumber,
    displayName,
    embeddedContact,
    size,
    lazyLoad,
    isCaller,
    fallback,
    showStatus,
}: Props) => {
    const chatUserExtensionKey = useAppSelector((state: State) =>
        selectExtensionKeyFromChatUserKey(state, chatUserKey),
    );
    const extensionKey = knownExtensionKey ?? chatUserExtensionKey;
    // The chatUserKey map only ever merges, so a key can outlive the extension
    // it points at. Asked for a missing one, ExtensionContactAvatar draws the
    // unknown glyph, which is worse than the initials we can still make. A
    // boolean, so this does not resubscribe to the churning contact itself.
    const colleagueContactIsStored = useAppSelector((state: State) =>
        Boolean(extensionKey && selectContact(state, extensionKey)),
    );

    const status = useAppSelector((state: State) =>
        showStatus && extensionKey
            ? selectExtensionStatus(state, extensionKey)
            : undefined,
    );

    const selectContactDataForNumber = useMemo(
        makeSelectContactDataForNumber,
        [],
    );
    // Skipped once the chatUserKey has answered: this result comes out of a
    // list rebuilt on every extensions refetch, so subscribing to it when a
    // colleague already won would redraw for nothing.
    const numberContact = useAppSelector((state: State) =>
        phoneNumber && !colleagueContactIsStored
            ? selectContactDataForNumber(state, phoneNumber)
            : undefined,
    );

    const contact = contactNamesSomebody(embeddedContact)
        ? embeddedContact
        : numberContact;
    const name = sliceUUIDFromUserName(displayName || '');

    if (colleagueContactIsStored) {
        return (
            <ExtensionContactAvatar
                extensionKey={extensionKey}
                size={size}
                status={status}
                lazyLoad={lazyLoad}
            />
        );
    }
    if (contact) {
        return (
            <ContactAvatar contact={contact} size={size} lazyLoad={lazyLoad} />
        );
    }
    if (fallback) {
        return <>{fallback}</>;
    }
    // Nobody resolved, and nothing to make initials from.
    if (isCaller || !name) {
        return (
            <UserAvatar size={size} className="bg-gray-600">
                {PERSON_GLYPH}
            </UserAvatar>
        );
    }
    return (
        <UserAvatar size={size} className={memberColorClass(name)}>
            {initialsFromName(name)}
        </UserAvatar>
    );
};

/** Home embeds a contact on every member, but for an unresolved participant it
 *  only wraps their number — it names nobody, so a real lookup must still run. */
const contactNamesSomebody = (contact?: TypedMap<Contact>) =>
    !!contact &&
    (!!contact.get('key') ||
        !!contact.get('firstName') ||
        !!contact.get('lastName'));

const initialsFromName = (name: string) => {
    const [firstName, lastName] = name.split(' ');
    return initialsFromNames(firstName, lastName);
};

// memo: a tile re-renders on every track toggle and speaking change, and on a
// camera-off tile this subtree is all there is to draw. Its props are identity
// and presentation, so they sit still through those.
export default React.memo(MeetingContactAvatar);
